import { Component } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { KPI_URLS } from "../../module/kpi-module-routing.module";
import { filter, switchMap, tap } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { OrganizationsService } from "../../../organization/components/service/organizations.service";
import { Organization } from "../../../organization/dto/Organization";
import { CompanyBackendService } from "../../../pp/service/company-backend.service";
import { CompanyMiniDto } from "../../../recovery/component/recovery-company/dto/CompanyWithOrgDto";
import { TabType } from "../../../shared/component/tabs-with-counter/tabs-with-counter.component";

@Component({
  selector: 'app-kpi-metrics-main',
  templateUrl: './kpi-metrics-main.component.html',
  styleUrl: './kpi-metrics-main.component.scss',
})
export class KpiMetricsMainComponent {
  protected readonly TabType = TabType;

  tabs = new Tabs(this.router.url);
  selectedOrg: Organization;
  companies: CompanyMiniDto[];
  showAiModal = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private orgService: OrganizationsService,
    private companyService: CompanyBackendService,
  ) {
    this.initSubscriptions();
  }

  switchTab(tab: typeof PortfolioAnalysisTab[number]): void {
    if (tab === this.tabs.selected) {
      return;
    }
    this.tabs.selected = tab;
    this.tabs.updateSelectedIndex();
    switch (tab) {
      case TabsNames.CrossMetric:
        this.router.navigate(
          [KPI_URLS.base + '/' + KPI_URLS._crossPortfolio + '/' + KPI_URLS._crossMetric],
          { queryParams: this.route.snapshot.queryParams });
        break;
      case TabsNames.CrossCompany:
        this.router.navigate(
          [KPI_URLS.base + '/' + KPI_URLS._crossPortfolio + '/' + KPI_URLS._crossCompany],
          { queryParams: this.route.snapshot.queryParams });
        break;
    }
  }

  openAi(): void {
    this.showAiModal = true;
  }

  private async fetchCompanies(): Promise<void> {
    this.companies = await this.companyService.getMini(this.selectedOrg.uuid);
  }

  private initSubscriptions(): void {
    this.orgService.selected$.pipe(
      filter((org) => Boolean(org)),
      tap((org) => this.selectedOrg = org),
      switchMap(() => this.fetchCompanies()),
      takeUntilDestroyed()
    ).subscribe();
    this.router.events.pipe(
      filter((event: NavigationEnd) => event instanceof NavigationEnd),
      tap(() => this.tabs.defineTabAfterRefresh(this.router.url)),
    ).subscribe();
  }
}

class Tabs {
  all: typeof PortfolioAnalysisTab[number][];
  selected: typeof PortfolioAnalysisTab[number] = TabsNames.CrossMetric;
  selectedIndex: number;

  constructor(url: string) {
    this.initTabs(url);
  }

  initTabs(url: string): void {
    this.all = Object.values(TabsNames);
    this.defineTabAfterRefresh(url);
  }

  defineTabAfterRefresh(url: string): void {
    this.selected = this.getTabByURL(url);
    this.updateSelectedIndex();
  }

  getTabByURL(url: string): typeof PortfolioAnalysisTab[number] {
    if (!url) {
      return;
    }
    if (url.includes(KPI_URLS._crossMetric)) {
      return TabsNames.CrossMetric;
    }
    if (url.includes(KPI_URLS._crossCompany)) {
      return TabsNames.CrossCompany;
    }
    return null;
  }

  updateSelectedIndex(): void {
    this.selectedIndex = this.all.indexOf(this.selected);
  }
}

const TabsNames = {
  CrossMetric: 'Cross-metric',
  CrossCompany: 'Cross-company',
};
const PortfolioAnalysisTab = [TabsNames.CrossMetric, TabsNames.CrossCompany] as const;
